(() => {
  if (window.lcSalesHubLoaded) return;
  window.lcSalesHubLoaded = true;
  const allowedSorts = new Set(['manual', 'created-descending', 'best-selling']);
  const endpoint = (path) => {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid collection origin');
    url.searchParams.set('view', 'sales-hub-products');
    url.searchParams.set('filter.v.availability', '1');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  const read = async (url, signal) => {
    const response = await fetch(url, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to load clearance');
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const result = document.querySelector('[data-sale-response]');
    if (!result) throw new Error('Missing collection results');
    result.querySelectorAll('script:not([data-sale-facets])').forEach(script => script.remove());
    return result;
  };
  const typeCounts = result => JSON.parse(result.querySelector('[data-sale-facets]')?.textContent || '{}');
  const eligible = (root, counts) => {
    if (root.dataset.us !== 'true') return;
    root.querySelectorAll('[data-sale-type], [data-sale-filter]').forEach(item => {
      const type = item.dataset.saleType ?? item.dataset.saleFilter;
      item.hidden = Boolean(type) && (counts[type] || 0) < 12;
    });
  };
  function initCarousel(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const track = root.querySelector('[data-sale-track]');
    const previous = root.querySelector('[data-sale-prev]');
    const next = root.querySelector('[data-sale-next]');
    const update = () => {
      const overflow = track.scrollWidth > track.clientWidth + 2;
      previous.hidden = next.hidden = !overflow;
      previous.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    const move = direction => {
      const tile = track.querySelector('[data-sale-type]:not([hidden])');
      const step = (tile?.getBoundingClientRect().width || 195) + parseFloat(getComputedStyle(track).columnGap);
      track.scrollBy({ left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(track);
    new MutationObserver(update).observe(track, { attributes: true, subtree: true, attributeFilter: ['hidden'] });
    update();
  }
  async function initGrid(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const results = root.querySelector('[data-sale-results]');
    const count = root.querySelector('[data-sale-count]');
    const sort = root.querySelector('[data-sale-sort]');
    const more = root.querySelector('[data-sale-more]');
    const error = root.querySelector('[data-sale-error]');
    const chips = [...root.querySelectorAll('[data-sale-filter]')];
    let controller, nextUrl, requestId = 0, counts;
    let type = new URL(location.href).searchParams.get('sale_type') || '';
    if (!chips.some(chip => chip.dataset.saleFilter === type)) type = '';
    const requestedSort = new URL(location.href).searchParams.get('sale_sort');
    sort.value = allowedSorts.has(requestedSort) ? requestedSort : root.dataset.defaultSort;
    const selected = () => chips.forEach(chip => {
      const active = chip.dataset.saleFilter === type;
      chip.setAttribute('aria-current', String(active));
      chip.classList.toggle('border-primary', active);
      chip.classList.toggle('border-[#c4c6cc]', !active);
    });
    const updateURL = () => {
      const url = new URL(location.href);
      type ? url.searchParams.set('sale_type', type) : url.searchParams.delete('sale_type');
      url.searchParams.set('sale_sort', sort.value);
      history.replaceState(null, '', url);
    };
    async function load(append = false) {
      controller?.abort(); controller = new AbortController();
      const id = ++requestId;
      results.setAttribute('aria-busy', 'true'); more.disabled = true; error.hidden = true;
      try {
        const url = endpoint(append ? nextUrl : root.dataset.collectionUrl);
        if (!append) {
          url.searchParams.set('sort_by', sort.value);
          if (type) url.searchParams.set('filter.p.product_type', type);
        }
        const result = await read(url, controller.signal);
        if (id !== requestId || !root.isConnected) return;
        if (!counts && !type) {
          counts = typeCounts(result); eligible(root, counts);
          document.querySelectorAll('[data-sale-clearance]').forEach(clearance => {
            if (clearance.dataset.collectionUrl === root.dataset.collectionUrl) eligible(clearance, counts);
          });
        }
        const items = result.querySelector('[data-sale-items]');
        if (!items) throw new Error('Missing product list');
        if (append) {
          const existing = new Set([...results.querySelectorAll('[data-sale-product]')].map(item => item.dataset.saleProduct));
          [...items.children].forEach(item => { if (!existing.has(item.dataset.saleProduct)) results.querySelector('[data-sale-items]').append(item); });
        } else results.replaceChildren(items);
        count.textContent = result.dataset.countLabel;
        nextUrl = result.dataset.next || null;
        more.hidden = !nextUrl;
        selected();
        window.initWishlist?.();
      } catch (failure) {
        if (failure.name === 'AbortError' || id !== requestId) return;
        error.hidden = false;
        if (!results.querySelector('[data-sale-product]')) count.textContent = '';
      } finally {
        if (id === requestId) { results.setAttribute('aria-busy', 'false'); more.disabled = false; }
      }
    }
    root.addEventListener('click', event => {
      const chip = event.target.closest('[data-sale-filter]');
      if (!chip || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault(); type = chip.dataset.saleFilter; updateURL(); load();
    });
    sort.addEventListener('change', () => { updateURL(); load(); });
    more.addEventListener('click', () => { if (nextUrl) load(true); });
    selected();
    if (type && root.dataset.us === 'true') {
      try {
        counts = typeCounts(await read(endpoint(root.dataset.collectionUrl)));
        eligible(root, counts);
        if ((counts[type] || 0) < 12) type = '';
      } catch { type = ''; }
    }
    load();
  }
  function start(scope = document) {
    scope.querySelectorAll('[data-sale-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-sale-grid]').forEach(initGrid);
    scope.querySelectorAll('[data-sale-clearance][data-us="true"]').forEach(async root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      try { eligible(root, typeCounts(await read(endpoint(root.dataset.collectionUrl)))); } catch { /* Unverified stock types remain hidden. */ }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start());
  else start();
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
